import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideRefreshCw,
  lucideLogOut,
  lucideCircleAlert,
  lucideLock,
  lucideUser,
} from '@ng-icons/lucide';

import { UI } from '../../components/ui';
import { TurnstileComponent } from '../../components/turnstile/turnstile.component';
import { AuthService } from '../../services/auth.service';
import {
  MERCHANT_PORTAL_NOTICE,
  merchantPortalUrl,
  redirectMerchantUserToPortal,
} from '../../utils/merchant-portal';

/**
 * Login page: username/password form, redirects to dashboard on success.
 * Merchant-role users are sent to the Merchant Portal instead.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [FormsModule, NgIcon, RouterLink, TurnstileComponent, ...UI],
  templateUrl: './login.component.html',
  viewProviders: [
    provideIcons({
      lucideUser,
      lucideLock,
      lucideLogOut,
      lucideCircleAlert,
      lucideRefreshCw,
    }),
  ],
})
export class LoginComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly otpRequired = signal(false);
  readonly otpMaskedDestination = signal<string | null>(null);
  readonly merchantPortalUrl = merchantPortalUrl();
  readonly merchantRedirectUrl = signal<string | null>(null);
  private otpId: string | null = null;

  username = '';
  password = '';
  otpCode = '';
  turnstileToken = '';
  private readonly turnstileWidget = viewChild<TurnstileComponent>('turnstileWidget');

  private handleLoginError(err: unknown): void {
    this.loading.set(false);
    this.resetTurnstile();
    if (err instanceof HttpErrorResponse && err.status === 403) {
      const portalUrl = err.error?.merchant_portal_url || this.merchantPortalUrl;
      const detail = typeof err.error?.detail === 'string' ? err.error.detail : '';
      if (err.error?.merchant_portal_url || /merchant portal/i.test(detail)) {
        this.merchantRedirectUrl.set(portalUrl);
        this.error.set(
          detail || 'This account is not authorized for the admin console. Please use the Merchant Portal.'
        );
        return;
      }
    }
    this.merchantRedirectUrl.set(null);
    this.error.set(this.loginErrorMessage(err));
  }

  private loginErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const detail = err.error?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
      if (err.status === 401) {
        return 'Invalid username or password.';
      }
    }
    return err instanceof Error ? err.message : 'Login failed.';
  }

  goToMerchantPortal(): void {
    const target = this.merchantRedirectUrl() || this.merchantPortalUrl;
    window.location.assign(target);
  }

  private resetTurnstile(): void {
    this.turnstileWidget()?.reset();
  }

  private navigatePostLogin(): void {
    const user = this.auth.user();
    if (redirectMerchantUserToPortal(user)) {
      this.info.set(MERCHANT_PORTAL_NOTICE);
      this.loading.set(false);
      return;
    }
    if (user?.password_change_required) {
      this.router.navigate(['/change-password']);
      return;
    }
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      this.router.navigateByUrl(returnUrl);
      return;
    }
    this.router.navigate(['/app']);
  }

  onSubmit(): void {
    this.error.set(null);
    this.info.set(null);
    this.merchantRedirectUrl.set(null);
    this.otpRequired.set(false);
    this.otpMaskedDestination.set(null);
    this.otpId = null;
    if (!this.username.trim() || !this.password) {
      this.error.set('Please enter username and password.');
      return;
    }
    if (!this.turnstileToken) {
      this.error.set('Please complete the security check.');
      return;
    }
    this.loading.set(true);
    this.auth.login(this.username, this.password, this.turnstileToken).subscribe({
      next: (result) => {
        if (!result) {
          this.loading.set(false);
          this.resetTurnstile();
          this.error.set('Invalid username or password.');
          return;
        }
        if (result.kind === 'otp_required') {
          this.loading.set(false);
          this.otpRequired.set(true);
          this.otpId = result.otp_id;
          this.otpMaskedDestination.set(result.masked_destination);
          return;
        }
        this.navigatePostLogin();
      },
      error: (err) => {
        this.handleLoginError(err);
      },
    });
  }

  onVerifyOtp(): void {
    this.error.set(null);
    this.info.set(null);
    if (!this.otpId) {
      this.error.set('No OTP challenge found. Please sign in again.');
      return;
    }
    if (this.otpCode.trim().length < 6) {
      this.error.set('Please enter the OTP code.');
      return;
    }
    if (this.loading()) {
      return;
    }
    this.loading.set(true);
    this.auth.verifyLoginOtp(this.otpId, this.otpCode.trim()).subscribe({
      next: (user) => {
        this.loading.set(false);
        if (user) {
          this.navigatePostLogin();
          return;
        }
        this.error.set('Invalid or expired OTP code.');
      },
      error: (err) => {
        this.handleLoginError(err);
      },
    });
  }
}
